/**
 * Session Manager for tracking user sessions across page reloads
 *
 * Responsibilities:
 * - Generate unique session IDs
 * - Persist session to localStorage
 * - Validate session age and expiration
 * - Handle session reset on booking completion
 */

interface StoredSession {
    sessionId: string;
    sessionStartTime: number;
    lastActivityTime: number;
    navigationChain: string[];
}

export class SessionManager {
    private sessionId!: string;
    private sessionStartTime!: number;
    private lastActivityTime!: number;
    private navigationChain: string[] = [];

    private readonly STORAGE_KEY = "page_tracking_session";
    private readonly MAX_SESSION_AGE_MS = 30 * 60 * 1000; // 30 minutes of inactivity
    private readonly MAX_NAVIGATION_CHAIN = 50; // Keep last 50 pages to prevent unbounded growth

    constructor() {
        const stored = this.loadFromStorage();

        if (stored && this.isSessionValid(stored)) {
            // Restore existing session
            this.sessionId = stored.sessionId;
            this.sessionStartTime = stored.sessionStartTime;
            this.lastActivityTime = stored.lastActivityTime;
            this.navigationChain = stored.navigationChain || [];
        } else {
            // Create new session
            this.createNewSession();
        }
    }

    /**
     * Get current session ID (read-only operation)
     */
    getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Get session start time
     */
    getSessionStartTime(): number {
        return this.sessionStartTime;
    }

    /**
     * Get navigation chain
     */
    getNavigationChain(): string[] {
        return [...this.navigationChain];
    }

    /**
     * Add page to navigation chain and update activity
     */
    addToNavigationChain(pageName: string): void {
        this.navigationChain.push(pageName);

        // Trim to prevent unbounded growth
        if (this.navigationChain.length > this.MAX_NAVIGATION_CHAIN) {
            this.navigationChain = this.navigationChain.slice(-this.MAX_NAVIGATION_CHAIN);
        }

        this.updateActivity();
        this.saveToStorage();
    }

    /**
     * Reset session (e.g., after booking completion or abandonment)
     */
    resetSession(): void {
        this.createNewSession();
    }

    /**
     * Check if session is still valid based on last activity
     */
    isActive(): boolean {
        const inactivityDuration = Date.now() - this.lastActivityTime;
        return inactivityDuration < this.MAX_SESSION_AGE_MS;
    }

    /**
     * Get session age in milliseconds
     */
    getSessionAge(): number {
        return Date.now() - this.sessionStartTime;
    }

    /**
     * Update last activity timestamp and persist
     */
    private updateActivity(): void {
        this.lastActivityTime = Date.now();
        this.saveToStorage();
    }

    /**
     * Create a new session
     */
    private createNewSession(): void {
        this.sessionId = this.generateSessionId();
        this.sessionStartTime = Date.now();
        this.lastActivityTime = Date.now();
        this.navigationChain = [];
        this.saveToStorage();
    }

    /**
     * Generate unique session ID
     */
    private generateSessionId(): string {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 11);
        return `session_${timestamp}_${random}`;
    }

    /**
     * Load session from localStorage
     */
    private loadFromStorage(): StoredSession | null {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (!stored) return null;

            const session = JSON.parse(stored) as StoredSession;
            return session;
        } catch (error) {
            console.warn("Failed to load session from storage:", error);
            return null;
        }
    }

    /**
     * Save session to localStorage
     */
    private saveToStorage(): void {
        try {
            const session: StoredSession = {
                sessionId: this.sessionId,
                sessionStartTime: this.sessionStartTime,
                lastActivityTime: this.lastActivityTime,
                navigationChain: this.navigationChain,
            };

            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(session));
        } catch (error) {
            console.warn("Failed to save session to storage:", error);
        }
    }

    /**
     * Check if stored session is still valid
     */
    private isSessionValid(session: StoredSession): boolean {
        if (!session.sessionId || !session.lastActivityTime) {
            return false;
        }

        const age = Date.now() - session.lastActivityTime;
        return age < this.MAX_SESSION_AGE_MS;
    }
}

export const sessionManager = new SessionManager();
